// Tuition-plan admin helpers.

import { supabase, isSupabaseConfigured } from "./supabase";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export async function getTuitionPlanForProfile(billingProfileId) {
  if (!isSupabaseConfigured) return null;
  const tryFetch = (cols) =>
    supabase
      .from("tuition_plans")
      .select(cols)
      .eq("billing_profile_id", billingProfileId)
      .maybeSingle();

  let { data, error } = await tryFetch(
    "id, organization_id, billing_profile_id, cadence, day_of_month, day_of_week, auto_issue, is_active, pause_until, stripe_subscription_id, next_run_on, last_run_at, last_result, notes, created_at, updated_at",
  );
  if (error && /column .* does not exist/i.test(error.message || "")) {
    ({ data, error } = await tryFetch(
      "id, organization_id, billing_profile_id, cadence, day_of_month, auto_issue, is_active, pause_until, stripe_subscription_id, next_run_on, last_run_at, last_result, notes, created_at, updated_at",
    ));
  }
  if (error) throw error;
  return data || null;
}

export async function upsertTuitionPlan({
  billingProfileId,
  cadence = "monthly",
  dayOfMonth = 1,
  dayOfWeek = null,
  autoIssue = false,
  isActive = true,
  pauseUntil = null,
  notes = null,
}) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { data, error } = await supabase.rpc("rpc_upsert_tuition_plan", {
    p_billing_profile_id: billingProfileId,
    p_cadence: cadence,
    p_day_of_month: dayOfMonth,
    p_auto_issue: autoIssue,
    p_is_active: isActive,
    p_pause_until: pauseUntil,
    p_notes: notes,
    p_day_of_week: dayOfWeek,
  });
  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}

export async function deleteTuitionPlan(planId) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { error } = await supabase.rpc("rpc_delete_tuition_plan", {
    p_plan_id: planId,
  });
  if (error) throw error;
}

/**
 * Admin "Run now" — calls rpc_run_tuition_plans directly (the admin is
 * already signed in). The scheduled runner uses the same RPC via the
 * FastAPI cron endpoint for system-triggered runs.
 */
export async function runTuitionPlanNow(planId) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { data, error } = await supabase.rpc("rpc_run_tuition_plans", {
    p_as_of: new Date().toISOString().slice(0, 10),
    p_plan_id: planId,
    p_bypass_role_check: false,
  });
  if (error) throw error;
  return (data || []).map(normaliseRunRow);
}

function normaliseRunRow(row) {
  return {
    plan_id:            row.out_plan_id ?? row.plan_id ?? null,
    billing_profile_id: row.out_billing_profile_id ?? row.billing_profile_id ?? null,
    family_id:          row.out_family_id ?? row.family_id ?? null,
    family_name:        row.out_family_name ?? row.family_name ?? "",
    status:             row.out_status ?? row.status ?? null,
    reason:             row.out_reason ?? row.reason ?? null,
    invoice_id:         row.out_invoice_id ?? row.invoice_id ?? null,
    issued:             row.out_issued ?? row.issued ?? false,
    amount_cents:       row.out_amount_cents ?? row.amount_cents ?? 0,
    next_run_on:        row.out_next_run_on ?? row.next_run_on ?? null,
    cadence:            row.out_cadence ?? row.cadence ?? null,
  };
}

/**
 * Org-wide list of tuition plans with derived readiness hints.
 * Returns rows ready for the /admin/billing/plans table.
 */
export async function listTuitionPlansWithContext() {
  if (!isSupabaseConfigured) return [];

  // Pre-migration fallback: drop newly-added columns if PostgREST 400s.
  const fullSelect = `
      id, organization_id, billing_profile_id, cadence, day_of_month, day_of_week,
      auto_issue, is_active, pause_until, next_run_on,
      last_run_at, last_result, notes, created_at, updated_at,
      billing_profile:billing_profiles(
        id, family_id, billing_email, status,
        monthly_tuition_override_cents, weekly_tuition_override_cents,
        sibling_discount_percent_override,
        family:families(id, display_name)
      )
    `;
  const legacySelect = `
      id, organization_id, billing_profile_id, cadence, day_of_month,
      auto_issue, is_active, pause_until, next_run_on,
      last_run_at, last_result, notes, created_at, updated_at,
      billing_profile:billing_profiles(
        id, family_id, billing_email, status,
        monthly_tuition_override_cents, sibling_discount_percent_override,
        family:families(id, display_name)
      )
    `;
  const tryFetch = (sel) =>
    supabase.from("tuition_plans").select(sel).order("next_run_on", { ascending: true });
  let { data, error } = await tryFetch(fullSelect);
  if (error && /column .* does not exist/i.test(error.message || "")) {
    ({ data, error } = await tryFetch(legacySelect));
  }
  if (error) throw error;
  const rows = data || [];

  // Resolve each family's child count + tuition-configured count in one trip
  const familyIds = rows.map((r) => r.billing_profile?.family_id).filter(Boolean);
  let childRollup = new Map();
  if (familyIds.length > 0) {
    const tryKids = (sel) =>
      supabase.from("children").select(sel).in("family_id", familyIds);
    let { data: kids, error: kErr } = await tryKids(
      "id, family_id, classroom_id, withdrawn_at, tuition_multiplier, schedule_pattern, classroom:classrooms(monthly_tuition_cents, weekly_tuition_cents)",
    );
    if (kErr && /column .* does not exist/i.test(kErr.message || "")) {
      ({ data: kids } = await tryKids(
        "id, family_id, classroom_id, withdrawn_at, classroom:classrooms(monthly_tuition_cents)",
      ));
    }
    for (const kid of kids || []) {
      const cur = childRollup.get(kid.family_id) || {
        total: 0,
        eligible: 0,
        tuitionReady: 0,
      };
      cur.total += 1;
      if (!kid.withdrawn_at && kid.classroom_id) {
        cur.eligible += 1;
        if (kid.classroom?.monthly_tuition_cents > 0) cur.tuitionReady += 1;
      }
      childRollup.set(kid.family_id, cur);
    }
  }

  return rows.map((r) => {
    const stats = childRollup.get(r.billing_profile?.family_id) || {
      total: 0,
      eligible: 0,
      tuitionReady: 0,
    };
    return { ...r, _childStats: stats, _health: deriveHealth(r, stats) };
  });
}

function deriveHealth(plan, stats) {
  const reasons = [];
  if (!plan.billing_profile) {
    reasons.push("missing_billing_profile");
    return { ok: false, reasons };
  }
  if (plan.billing_profile.status !== "active") {
    reasons.push(`billing_profile_${plan.billing_profile.status}`);
  }
  if (!plan.is_active) reasons.push("plan_inactive");
  // Note: a `paused` plan is NOT counted as unhealthy — pausing is a
  // deliberate admin decision. It shows up in the Paused KPI + filter
  // chip instead.
  if (stats.eligible === 0) reasons.push("no_eligible_children");
  if (stats.eligible > 0 && stats.tuitionReady === 0
      && plan.billing_profile.monthly_tuition_override_cents == null) {
    reasons.push("no_tuition_configured");
  }
  return { ok: reasons.length === 0, reasons };
}

export const PLAN_HEALTH_REASON_LABELS = {
  missing_billing_profile: "Missing billing profile",
  billing_profile_paused: "Billing profile paused",
  billing_profile_closed: "Billing profile closed",
  plan_inactive: "Plan is inactive",
  paused: "Paused",
  no_eligible_children: "No eligible children",
  no_tuition_configured: "No tuition configured",
};

export function planStatusLabel(plan) {
  if (!plan) return "No plan";
  if (!plan.is_active) return "Inactive";
  const pausedUntil = plan.pause_until ? new Date(plan.pause_until) : null;
  if (pausedUntil && pausedUntil > new Date()) {
    return `Paused until ${pausedUntil.toLocaleDateString()}`;
  }
  return plan.auto_issue ? "Active · auto-issue" : "Active · draft only";
}

// 0=Sunday … 6=Saturday — matches Postgres `extract(dow ...)` semantics.
export const WEEKDAY_LABELS = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];
export const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function cadenceScheduleLabel(plan) {
  if (!plan) return null;
  if (plan.cadence === "weekly") {
    const dow = Number.isInteger(plan.day_of_week) ? plan.day_of_week : null;
    return dow == null
      ? "Weekly"
      : `Weekly · every ${WEEKDAY_LABELS[dow] || "—"}`;
  }
  return `Monthly · day ${plan.day_of_month || 1}`;
}

/**
 * Admin one-click trigger of the cron endpoint (for dry-run / "check the
 * whole system" flows). Requires CRON_SECRET in env — NOT for everyday
 * use. Currently not wired to UI; kept here for future admin tooling.
 */
export async function runAllDuePlansViaCron(cronSecret) {
  const resp = await fetch(`${API_BASE}/api/cron/run-tuition-plans`, {
    method: "POST",
    headers: { "X-Cron-Secret": cronSecret },
  });
  if (!resp.ok) {
    const msg = await resp.text();
    throw new Error(msg || `Cron run failed (${resp.status})`);
  }
  return resp.json();
}
